import {
  ADJUSTMENT_REASON_LABELS,
  type FieldDefinition,
} from "@estelle/domain";

/**
 * The fields the Inventory module offers to the filter builder, the column
 * chooser and the sort control, for the stock-levels and low-stock screens
 * (FR-INV-4).
 *
 * Order matters — the default columns are every displayable field, in this
 * order, and a wide list scrolls sideways — so what the list is for comes
 * first: what, where, how much, and the threshold that figure is judged by.
 * The threshold is the one a person can now change from here (#521); as the
 * last of twelve columns it was a scroll to the right of the table, 233px past
 * the edge at 1440 and most of a screen at 820.
 *
 * `available` covers "stock range" on its own: it is a `number` field, and
 * the operator registry already gives every `number` field `between`. A
 * second, range-only field would just be this one field with a narrower
 * menu, so FR-INV-4's "stock range" is this field, not a new one.
 *
 * `vendor`, `productType` and `tags` are declared here because FR-INV-4
 * names them, but `InventoryRow.product` is typed as
 * `Pick<Product, "id" | "title" | "vendor" | "featuredImageUrl">` —
 * `productType` and `tags` are not on it, so the row this module is handed
 * cannot show them: as columns they were a dash on every row, which reads as
 * missing stock data (#503). They are `displayable: false` — in the filter
 * builder, where the adapters project them themselves (the API joins the
 * product, the fixture adapter looks it up), and not in the table, the column
 * chooser or the CSV. The same flag Orders' `product` field uses, and what
 * Low Stock did for itself first (#487).
 */
export const INVENTORY_FIELDS: FieldDefinition[] = [
  /*
    One warehouse view (#602 part C): what Shopify's own inventory screen shows
    for a location. Product and SKU, then Unavailable, Committed, Available,
    On hand and In transit; the rest are in the column chooser. The warehouse is
    chosen by the picker above the table, so its column is hidden by default.
  */
  { name: "productTitle", label: "Product", type: "text", sortable: true },
  { name: "sku", label: "SKU", type: "text", sortable: true },
  {
    name: "unavailable",
    label: "Unavailable",
    type: "number",
    sortable: true,
  },
  { name: "committed", label: "Committed", type: "number", sortable: true },
  { name: "available", label: "Available", type: "number", sortable: true },
  { name: "onHand", label: "On hand", type: "number", sortable: true },
  { name: "inTransit", label: "In transit", type: "number", sortable: true },
  {
    name: "variantTitle",
    label: "Variant",
    type: "text",
    sortable: true,
    defaultHidden: true,
  },
  {
    name: "warehouse",
    label: "Warehouse",
    type: "reference",
    sortable: true,
    defaultHidden: true,
  },
  {
    name: "lowStockThreshold",
    label: "Low stock threshold",
    type: "number",
    defaultHidden: true,
  },
  {
    name: "lastSyncedAt",
    label: "Last synced",
    type: "datetime",
    sortable: true,
    defaultHidden: true,
  },
  { name: "hsnCode", label: "HSN code", type: "text", defaultHidden: true },
  { name: "vendor", label: "Vendor", type: "text", defaultHidden: true },
  {
    name: "productType",
    label: "Product type",
    type: "text",
    displayable: false,
  },
  { name: "tags", label: "Tags", type: "tag", displayable: false },
  {
    name: "belowThreshold",
    label: "Below threshold",
    type: "boolean",
    defaultHidden: true,
  },
  // Nothing left to sell here (#633): available at or below zero.
  {
    name: "outOfStock",
    label: "Out of stock",
    type: "boolean",
    defaultHidden: true,
  },
];

/**
 * Fields for the adjustment list and the create form's column chooser.
 *
 * `reason` is `enum` with no `options`: `AdjustmentReason` is an open union
 * on purpose (FR-INV-7), and the filter builder's `enum` value input only
 * renders the fixed picker when `options` is set — without it, `ValueInput`
 * falls back to free text and `renderCell` falls back to `humanise()`. That
 * is what lets a sixth reason someone invents next month filter and display
 * correctly with no change here. `status` is the opposite case: `WriteStatus`
 * is a closed three-value union, so its `options` are declared for a proper
 * picker and a stable badge label.
 *
 * `variantId` and `warehouseId` are the raw ids `StockAdjustment` carries —
 * `InventoryRepository.listAdjustments` returns `StockAdjustment` itself,
 * not a joined row the way `list` and `listLowStock` return `InventoryRow`.
 * `Adjustments.tsx` resolves them to a SKU and a warehouse code for display
 * via its own lookup, built from `inventory.list`, rather than this file
 * pretending the row carries names it does not.
 */
export const ADJUSTMENT_FIELDS: FieldDefinition[] = [
  { name: "createdAt", label: "Date", type: "datetime", sortable: true },
  { name: "reason", label: "Reason", type: "enum", sortable: true },
  { name: "delta", label: "Delta", type: "number", sortable: true },
  {
    name: "status",
    label: "Status",
    type: "enum",
    sortable: true,
    options: [
      { value: "pending", label: "Pending" },
      { value: "accepted", label: "Accepted" },
      { value: "rejected", label: "Rejected" },
    ],
  },
  { name: "actorUserId", label: "Actor", type: "reference" },
  { name: "error", label: "Error", type: "text" },
  { name: "variantId", label: "Variant", type: "reference" },
  { name: "warehouseId", label: "Warehouse", type: "reference" },
  { name: "note", label: "Note", type: "text" },
];

/**
 * Reasons offered as one-click choices on the adjustment form.
 *
 * A convenience list, not a schema: the form still accepts any string
 * through the same field (see `ReasonPicker` in `screens/Adjustments.tsx`),
 * because `AdjustmentReason` is extensible and a form that could only submit
 * these five would be a code change away from the reason the team invents
 * next month.
 */
export const KNOWN_ADJUSTMENT_REASONS: { value: string; label: string }[] =
  Object.entries(ADJUSTMENT_REASON_LABELS).map(([value, label]) => ({
    value,
    label,
  }));
